import { useRef } from 'react';
import { REVEAL_EASE } from '../core/reveal';
import { WORD_DURATION_MS, scheduleWords, type WordSchedule } from './revealWordsSchedule';

/**
 * RevealWords — text that arrives word by word: each word fades in, rises a few
 * pixels and un-blurs, on the same easing as the rest of the reveal motion
 * (see core/reveal). Built for a reply that streams in: words already on
 * screen never replay, and new ones queue behind the last at a steady rhythm
 * instead of landing in bursts as network chunks arrive.
 *
 * Every word is in the layout from the start (just not visible yet), so the
 * bubble doesn't reflow word by word.
 */
const KEYFRAMES = '@keyframes sk-word-in{from{opacity:0;transform:translateY(6px);filter:blur(3px)}to{opacity:1;transform:translateY(0);filter:blur(0)}}';

export function RevealWords({ text, onDone }: { text: string; /** Every word on screen so far has finished appearing. Fires again if more words stream in and finish. */ onDone?: () => void }) {
  const schedule = useRef<WordSchedule>({ delays: [], nextSlot: 0 });
  const finished = useRef(0);
  // Words and the whitespace between them: spaces stay plain text, so lines wrap between words as usual.
  const tokens = text.split(/(\s+)/);
  const wordCount = tokens.filter((t) => t !== '' && !/^\s+$/.test(t)).length;
  scheduleWords(schedule.current, wordCount, typeof performance !== 'undefined' ? performance.now() : 0);

  let word = -1;
  return (
    <>
      <style>{KEYFRAMES}</style>
      {tokens.map((token, i) => {
        if (token === '' || /^\s+$/.test(token)) return token;
        word += 1;
        return (
          <span
            key={`${i}`}
            style={{
              display: 'inline-block',
              animation: `sk-word-in ${WORD_DURATION_MS}ms ${REVEAL_EASE} ${Math.round(schedule.current.delays[word])}ms both`,
            }}
            // Once it has landed, drop the animation so a finished word holds no filter or layer.
            onAnimationEnd={(e) => {
              e.currentTarget.style.animation = 'none';
              finished.current += 1;
              if (finished.current >= wordCount) onDone?.();
            }}
          >
            {token}
          </span>
        );
      })}
    </>
  );
}
